import { AccountBalanceOutlined } from "@mui/icons-material";
import { Card, CircularProgress } from "@mui/material";
import { grey } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import { isEmpty, isNil } from "lodash";
import { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { columnTypesDate } from "../../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import { columnTypesDollar } from "../../../_src_shared/utils/agGrid/columnTypes/columnTypesDollar";
import { columnTypesPercent } from "../../../_src_shared/utils/agGrid/columnTypes/columnTypesPercent";
import AgGridTableWrapper from "../../../components/agGrid/AgGridTableWrapper";
import SearchInput from "../../../components/inputs/SearchInput";
import Flx from "../../../components/layout/Flx";
import { DASHBOARD_SIDEBAR_TYPES } from "../../../components/sidebars/DashboardSidebar/DashboardSidebar";
import Txt from "../../../components/typography/Txt";
import EmptyStateCard from "../../../components/ui/EmptyStateCard";
import { sidebarOpen } from "../../../redux/reducers/sidebarReducer";

import { formatPhone } from "../../../_src_shared/utils/functions/formatPhone";
import SidebarAddBorrowerBankInfoButton from "../../../components/sidebars/DashboardSidebar/buttons/SidebarAddBorrowerBankInfoButton";
import ProfileSection from "../components/ProfileSection";

const ProfileBankInformationSection = ({ activeTab, keyName }) => {
  const profile = useSelector((state) => state?.profile);

  const bankAccounts = profile?.bankAccounts || [];

  return (
    <ProfileSection
      activeTab={activeTab}
      title={"Bank Information"}
      description="Add your bank account details to make them available for other things like draw requests."
      icon={<AccountBalanceOutlined sx={{ mr: 0.3 }} />}
      endContent={
        isEmpty(bankAccounts) ? null : <SidebarAddBorrowerBankInfoButton />
      }
    >
      <ContentRenderer activeTab={activeTab} />
    </ProfileSection>
  );
};

const ContentRenderer = () => {
  const profile = useSelector((state) => state?.profile);
  const dispatch = useDispatch();
  const bankAccounts = profile?.bankAccounts || [];

  if (isNil(profile)) {
    return (
      <Card sx={{ py: 3 }}>
        <Flx g={2} center fw column>
          <CircularProgress size={18} />
          <Txt secondary>Getting your profile information...</Txt>
        </Flx>
      </Card>
    );
  }

  if (isEmpty(bankAccounts)) {
    return (
      <EmptyStateCard
        icon={<AccountBalanceOutlined />}
        buttonStartIcon={null}
        title={"No Bank Information Added Yet"}
        description={
          "Add your bank account details to make them available for other things like draw requests."
        }
        buttonText={"Add Bank Information"}
        onClick={() =>
          dispatch(sidebarOpen(DASHBOARD_SIDEBAR_TYPES.ADD_BORROWER_BANK_INFO))
        }
      />
    );
  }

  if (!isEmpty(bankAccounts)) {
    return (
      <BorrowerBankInfoTable
        onRowClick={(data) =>
          dispatch(
            sidebarOpen(DASHBOARD_SIDEBAR_TYPES.EDIT_BORROWER_BANK_INFO, data),
          )
        }
        bankAccounts={bankAccounts}
      />
    );
  }
  return;
};

const BorrowerBankInfoTable = ({ bankAccounts = [], onRowClick }) => {
  const ref = useRef(null);

  const [quickFilter, setQuickFilter] = useState(null);

  const handleRowClick = useCallback(
    (params) => {
      if (onRowClick) {
        onRowClick(params.data);
      }
    },
    [onRowClick],
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Bank Name",
        field: "nameOfBank",
        width: 160,
        minWidth: 140,
      },
      {
        headerName: "Account Owner",
        field: "bankAccountOwnerName",
        width: 180,
        minWidth: 150,
      },
      {
        headerName: "Owner Type",
        field: "bankAccountOwnerType",
        width: 130,
        minWidth: 120,
      },
      {
        headerName: "Account Number",
        field: "bankAccountNumber",
        width: 170,
        minWidth: 150,
        valueFormatter: (params) => {
          const value = params?.value;
          if (!value) return "-";
          const last4 = value.slice(-4);
          return `****${last4}`;
        },
      },
      {
        headerName: "Routing Number",
        field: "bankRoutingNumber",
        width: 150,
        minWidth: 140,
        valueFormatter: (params) => {
          return params?.value || "-";
        },
      },
      {
        headerName: "Bank Phone",
        field: "bankPhoneNumber",
        width: 150,
        minWidth: 140,
        valueFormatter: (p) => formatPhone(p.value) || "-",
      },
      {
        headerName: "Preferred Payment Method",
        field: "preferredPaymentMethod",
        width: 190,
        minWidth: 170,
      },
      {
        headerName: "Last Wire Date",
        field: "lastWireDate",
        width: 140,
        type: "date",
      },
      {
        headerName: "Last Wire Verification Date",
        field: "lastWireVerificationDate",
        width: 200,
        type: "date",
      },
    ],
    [],
  );

  const columnTypes = useMemo(() => {
    return {
      ...columnTypesDate(),
      ...columnTypesDollar(),
      ...columnTypesPercent(),
    };
  }, []);

  const defaultColDef = useMemo(() => {
    return {
      filter: false,
    };
  }, []);

  return (
    <Flx
      column
      sx={{
        p: 0,
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      <Flx
        wrap
        fw
        jb
        ac
        gap={2}
        sx={{
          p: 2,
          background: grey[100],
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <SearchInput
          fullWidth
          value={quickFilter}
          sx={{ flexBasis: "400px", flexGrow: 1, background: "#ffffff" }}
          onChange={(e) => setQuickFilter(e)}
        />
      </Flx>
      <AgGridTableWrapper simpleTable>
        <AgGridReact
          ref={ref}
          rowData={bankAccounts}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          quickFilterText={quickFilter}
          columnTypes={columnTypes}
          domLayout={"autoHeight"}
          onRowClicked={handleRowClick}
        />
      </AgGridTableWrapper>
    </Flx>
  );
};

export default ProfileBankInformationSection;
